import { useEffect, useRef, useState } from "react";
import { createQrScanner } from "../lib/qrScanner";
import { playSuccessSound } from "../lib/sound";

/**
 * useQrScanner
 * - Scanページで使うためのReact hook
 * - 「開始/停止」「状態」「直近結果」「エラー」を提供
 */

export function useQrScanner({ readerId = "reader", stopAfterScan = true } = {}) {
  const scannerRef = useRef(null);

  const [status, setStatus] = useState("idle"); // idle | scanning | stopped | error
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    scannerRef.current = createQrScanner({
      readerId,
      onDecoded: async (text) => {
        setResult(text);
        playSuccessSound();
        if (stopAfterScan) {
          await scannerRef.current?.stop();
          setStatus("stopped");
        }
      },
      onError: () => {
        // 毎フレーム出ることがあるので、通常は何もしない
      },
    });

    // ページ離脱/コンポーネント破棄時に必ず停止（カメラ解放）
    return () => {
      scannerRef.current?.stop().catch(() => {});
      scannerRef.current = null;
    };
  }, [readerId, stopAfterScan]);

  const start = async () => {
    try {
      setError("");
      setStatus("scanning");
      await scannerRef.current?.start();
    } catch (e) {
      setStatus("error");
      setError(e?.message ?? String(e));
    }
  };

  const stop = async () => {
    try {
      await scannerRef.current?.stop();
      setStatus("stopped");
    } catch (e) {
      setStatus("error");
      setError(e?.message ?? String(e));
    }
  };

  return { status, result, error, start, stop };
}
